/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';

import { styles } from './button-divider.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface ButtonDividerProps {
    /**
     * Child elements
     */
    children?: ReactNode;

    /**
     * The size of the button
     *
     * 1. SMALL,
     * 2. MEDIUM,
     * 3. LARGE,
     * 4. XL
     *
     * @default MEDIUM
     */
    size?: ButtonSize;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Mouse click event handler.
     *
     * @param event {MouseEvent<HTMLElement>} The mouse event
     */
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;

    /**
     * Element blur event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The blur event
     */
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void;

    /**
     * Element focus event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The focus event
     */
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
}

const StyledButtonDivider = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const ButtonDivider: FunctionComponent<ButtonDividerProps> = ({
    children,
    onClick,
    onBlur,
    onFocus,
    ...otherProps
}: ButtonDividerProps) => {
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);
    const buttonSize = isSmallTablet ? ButtonSize.SMALL : ButtonSize.LARGE;

    return (
        <StyledButtonDivider {...otherProps}>
            <Button
                variant={ButtonVariant.TERTIARY}
                className="button"
                onClick={onClick}
                onBlur={onBlur}
                onFocus={onFocus}
                data-testid="button-divider__button"
                size={buttonSize}
            >
                {children}
            </Button>
        </StyledButtonDivider>
    );
};
